// src/pages/DashboardOperator.js
import React, { useState, useEffect, useMemo } from "react";
import {
  Box, Grid, Card, CardContent, Typography, Button, Stack, 
  Chip, Avatar, alpha, useTheme, CircularProgress
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  PlayArrowRounded,
  StopRounded,
  AssignmentTurnedInRounded,
  LocalLaundryService,
  PictureAsPdf,
  ReportProblem,
  TrendingUp,
  Speed,
  Scale,
  Warning,
  Analytics
} from "@mui/icons-material";
import { useI18n } from '../i18n/i18n';
import { getData, saveData, removeSessionItem } from '../services/storageService';
import InfoWidget from '../components/InfoWidget';

// 🎨 Спрощений компонент статистики
const StatCard = ({ title, value, unit, icon: Icon, color = "primary" }) => {
  const theme = useTheme();
  return (
    <Card sx={{ 
      height: '100%', 
      background: `linear-gradient(135deg, ${alpha(theme.palette[color].main, 0.1)} 0%, ${alpha(theme.palette[color].light, 0.05)} 100%)`,
      border: `1px solid ${alpha(theme.palette[color].main, 0.2)}`,
      borderRadius: 3,
      position: 'relative',
      overflow: 'visible'
    }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette[color].dark }}>
            {title}
          </Typography>
          <Avatar sx={{ 
            bgcolor: alpha(theme.palette[color].main, 0.1), 
            width: 48, 
            height: 48,
            border: `2px solid ${alpha(theme.palette[color].main, 0.2)}`
          }}>
            <Icon sx={{ color: theme.palette[color].main, fontSize: 24 }} />
          </Avatar>
        </Box>
        
        <Typography variant="h3" sx={{ 
          fontWeight: 800, 
          color: theme.palette[color].dark,
          mb: 1
        }}>
          {value}
          <Typography component="span" variant="h5" sx={{ 
            fontWeight: 600, 
            color: theme.palette.text.secondary,
            ml: 0.5
          }}>
            {unit}
          </Typography>
        </Typography>
      </CardContent>
    </Card>
  );
};

// 🎨 Спрощений компонент картки дії
const ActionCard = ({ title, description, icon: Icon, color = "primary", onClick, disabled = false }) => {
  const theme = useTheme();
  const { t } = useI18n();
  
  return (
    <Card sx={{ 
      height: '100%',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1,
      transition: 'all 0.3s ease',
      '&:hover': !disabled && {
        transform: 'translateY(-4px)',
        boxShadow: theme.shadows[8],
      },
      border: `2px solid ${alpha(theme.palette.divider, 0.1)}`,
      borderRadius: 3
    }}>
      <CardContent sx={{ p: 3, height: '100%' }} onClick={!disabled ? onClick : undefined}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
          <Avatar sx={{ 
            bgcolor: alpha(theme.palette[color].main, 0.1), 
            width: 56, 
            height: 56,
            mr: 2,
            border: `2px solid ${alpha(theme.palette[color].main, 0.2)}`
          }}>
            <Icon sx={{ color: theme.palette[color].main, fontSize: 28 }} />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Box>
        </Box>
        
        <Button
          variant="contained"
          color={color}
          disabled={disabled}
          fullWidth
          onClick={onClick}
          sx={{ 
            height: 48,
            borderRadius: 2,
            fontWeight: 700,
            textTransform: 'none',
            fontSize: '1rem'
          }}
        >
          {disabled ? t('dashboard.actions.disabled', 'Deshabilitado') : t('dashboard.actions.access', 'Acceder')}
        </Button>
      </CardContent>
    </Card>
  );
};

// 🎨 Компонент статусу зміни
const ShiftStatusCard = ({ activeShift, onCloseShift, onOpenShift, closingShift = false }) => {
  const theme = useTheme();
  const { t } = useI18n();

  if (!activeShift) {
    return (
      <Card sx={{ 
        background: `linear-gradient(135deg, ${alpha(theme.palette.warning.light, 0.1)} 0%, ${alpha(theme.palette.warning.main, 0.05)} 100%)`,
        border: `2px dashed ${alpha(theme.palette.warning.main, 0.3)}`,
        borderRadius: 3
      }}>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Warning sx={{ fontSize: 64, color: theme.palette.warning.main, mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: theme.palette.warning.dark }}>
            {t('dashboard.shift.no_active_shift')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t('dashboard.shift.no_shift_description')}
          </Typography>
          <Button
            variant="contained"
            color="warning"
            size="large"
            startIcon={<PlayArrowRounded />}
            onClick={onOpenShift}
            sx={{ 
              height: 56,
              px: 4,
              borderRadius: 3,
              fontWeight: 700,
              textTransform: 'none',
              fontSize: '1.1rem'
            }}
          >
            {t('dashboard.shift.open_shift')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ 
      background: `linear-gradient(135deg, ${alpha(theme.palette.success.light, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
      border: `2px solid ${alpha(theme.palette.success.main, 0.2)}`,
      borderRadius: 3
    }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Chip 
              label={t('dashboard.shift.active_shift')} 
              color="success" 
              variant="filled"
              sx={{ fontWeight: 700, mb: 1 }}
            />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {activeShift.turno} • {activeShift.fecha}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('dashboard.shift.operators')}: {activeShift.operators?.join(', ') || t('common.not_specified')}
            </Typography>
          </Box>
          <Avatar sx={{ 
            bgcolor: theme.palette.success.main, 
            width: 60, 
            height: 60 
          }}>
            <Speed sx={{ fontSize: 32 }} />
          </Avatar>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="error"
            startIcon={closingShift ? <CircularProgress size={20} color="inherit" /> : <StopRounded />}
            onClick={onCloseShift}
            disabled={closingShift}
            sx={{ 
              height: 48,
              px: 3,
              borderRadius: 2,
              fontWeight: 700,
              textTransform: 'none',
              flex: 1,
              minWidth: 200
            }}
          >
            {closingShift ? t('dashboard.shift.closing_shift') : t('dashboard.shift.close_shift')}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default function DashboardOperator({ user }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useI18n();
  const [activeShift, setActiveShift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [closingShift, setClosingShift] = useState(false);

  useEffect(() => {
    const fetchShiftData = async () => {
      try {
        const shiftData = await getData('current_shift');
        console.log('📋 Turno actual cargado:', shiftData);
        setActiveShift(shiftData);
      } catch (error) {
        console.error("Error fetching shift data:", error);
        setActiveShift(null);
      } finally {
        setLoading(false);
      }
    };
    
    fetchShiftData();
    const interval = setInterval(fetchShiftData, 30000);
    return () => clearInterval(interval);
  }, []);

  const productionSummary = useMemo(() => {
    if (!activeShift) {
        return { production: 0, rejection: 0, cycles: 0, clientWeight: 0 };
    }
    
    // Розрахунок виробництва тунелю
    const production = Object.values(activeShift.tunel || {}).reduce((acc, val) => acc + (Number(val) || 0), 0);
    
    // Розрахунок відходів тунелю
    const rejection = Object.values(activeShift.rech_tunel || {}).reduce((acc, val) => acc + (Number(val) || 0), 0);
    
    // Розрахунок циклів пральних машин
    const cycles = Object.values(activeShift.lav_ciclos || {}).reduce((acc, cyclesString) => {
      if (cyclesString && typeof cyclesString === 'string') {
        return acc + Math.max(0, cyclesString.split('|').length - 1);
      }
      return acc;
    }, 0);
    
    // Розрахунок ваги клієнтів
    const clientWeight = (activeShift.pesoCliente || []).reduce((acc, item) => acc + (Number(item.weight) || 0), 0);
    
    console.log('📊 Resumen de producción:', { production, rejection, cycles, clientWeight });
    
    return { production, rejection, cycles, clientWeight };
  }, [activeShift]);

  const handleCloseShift = async () => {
    if (!window.confirm(t('dashboard.shift.close_confirm'))) return;
    
    try {
      setClosingShift(true);
      console.log('🔵 === INICIANDO CIERRE DE TURNO ===');
      
      // Завантажити існуючі записи
      const allRecords = await getData('lainsa_records') || [];
      console.log('🔵 1. Registros existentes:', allRecords.length);
      
      // Створити унікальний ID для звіту
      const reportId = `shift_${activeShift.fecha}_${activeShift.turno}_${Date.now()}`;
      
      // Створити повний звіт
      const shiftReport = {
        ...activeShift,
        id: reportId,
        recordType: 'shift_report',
        closedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        // Обов'язкові поля для нормалізації
        date: activeShift.fecha,
        shift: activeShift.turno,
        operators: activeShift.operators || [],
        // Зберегти всі дані зміни
        tunel: activeShift.tunel || {},
        rech_tunel: activeShift.rech_tunel || {},
        lav_ciclos: activeShift.lav_ciclos || {},
        cont: activeShift.cont || {},
        pesoCliente: activeShift.pesoCliente || [],
        observaciones: activeShift.observaciones || '',
        // Додаткові поля для сумісності
        client: 'Varios',
        programCode: 'shift_report',
        machineId: 'tunel_lavanderia'
      };
      
      console.log('🔵 2. Reporte creado:', shiftReport.id);
      
      // Додати до існуючих записів
      const updatedRecords = [...allRecords, shiftReport];
      await saveData('lainsa_records', updatedRecords);
      
      // Перевірити збереження
      const verifyRecords = await getData('lainsa_records');
      const savedReport = verifyRecords?.find(r => r.id === reportId);
      
      if (!savedReport) {
        throw new Error('El reporte no se guardó en la base de datos');
      }
      
      // Очистити поточну зміну
      await removeSessionItem('current_shift');
      setActiveShift(null);
      
      console.log('🔵 3. Guardado exitoso, navegando a vista');
      console.log('🔵 3. ID para navegación:', reportId);
      
      alert(t('dashboard.shift.close_success'));
      
      // Навігація з закодованим ID
      navigate(`/view/${encodeURIComponent(reportId)}`);
      
    } catch (error) {
      console.error("❌ Error cerrando turno:", error);
      alert(t('dashboard.shift.close_error') + error.message);
    } finally {
      setClosingShift(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '60vh',
        flexDirection: 'column',
        gap: 3
      }}>
        <CircularProgress 
          size={60} 
          thickness={4}
          sx={{ color: theme.palette.primary.main }}
        />
        <Typography variant="h6" color="text.secondary">
          {t('common.loading')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
      {/* Заголовок */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" sx={{ 
          fontWeight: 800, 
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          mb: 1
        }}>
          {t('dashboard.welcome', { username: user?.username || t('common.operator') })}
        </Typography>
        <Typography variant="h6" color="text.secondary">
          {t('dashboard.subtitle')}
        </Typography>
      </Box>

      {/* InfoWidget */}
      <Box sx={{ mb: 4 }}>
        <InfoWidget />
      </Box>

      {/* Статус зміни */}
      <Box sx={{ mb: 4 }}>
        <ShiftStatusCard 
          activeShift={activeShift}
          onCloseShift={handleCloseShift}
          onOpenShift={() => navigate("/open-shift")}
          closingShift={closingShift}
        />
      </Box>

      {/* Статистика виробництва */}
      {activeShift && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
            {t('dashboard.production_summary')}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard 
                title={t('dashboard.stats.total_production')}
                value={productionSummary.production.toLocaleString()}
                unit={t('common.kg')}
                icon={TrendingUp}
                color="primary"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard 
                title={t('dashboard.stats.rejections')}
                value={productionSummary.rejection.toLocaleString()}
                unit={t('common.kg')}
                icon={Warning}
                color="error"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard 
                title={t('dashboard.stats.washer_cycles')}
                value={productionSummary.cycles.toLocaleString()}
                unit={t('dashboard.stats.cycles')}
                icon={LocalLaundryService}
                color="info"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard 
                title={t('dashboard.stats.client_weight')}
                value={productionSummary.clientWeight.toFixed(2)}
                unit={t('common.kg')}
                icon={Scale}
                color="success"
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Швидкі дії */}
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
        {t('dashboard.quick_actions')}
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <ActionCard
            title={t('dashboard.actions.production_form')}
            description={t('dashboard.actions.production_form_desc')}
            icon={AssignmentTurnedInRounded}
            color="primary"
            onClick={() => navigate("/form")}
            disabled={!activeShift}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <ActionCard
            title={t('dashboard.actions.machine_management')}
            description={t('dashboard.actions.machine_management_desc')}
            icon={LocalLaundryService}
            color="info"
            onClick={() => navigate("/machines")}
            disabled={!activeShift}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <ActionCard
            title={t('dashboard.actions.report_incident')}
            description={t('dashboard.actions.report_incident_desc')}
            icon={ReportProblem}
            color="warning"
            onClick={() => navigate('/incidents/create')}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <ActionCard
            title={t('dashboard.actions.export_pdf')}
            description={t('dashboard.actions.export_pdf_desc')}
            icon={PictureAsPdf}
            color="secondary"
            onClick={() => {/* PDF функціонал */}}
            disabled={!activeShift}
          />
        </Grid>
      </Grid>
    </Box>
  );
}


